/**
 * Convert an image file to WebP format
 * @param {File} file - The image file to convert
 * @param {number} quality - Quality of the WebP image (0-1), default 0.9
 * @returns {Promise<File>} - The converted WebP file
 */
export const convertToWebP = async (file, quality = 0.9) => {
  // If already WebP, return as is
  if (file.type === "image/webp") {
    return file;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        // Create canvas
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        // Draw image on canvas
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        // Convert to WebP
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Failed to convert image to WebP"));
              return;
            }

            // Create new File object with .webp extension
            const originalName = file.name.replace(/\.[^/.]+$/, "");
            const webpFile = new File([blob], `${originalName}.webp`, {
              type: "image/webp",
              lastModified: Date.now(),
            });

            resolve(webpFile);
          },
          "image/webp",
          quality,
        );
      };

      img.onerror = () => {
        reject(new Error("Failed to load image"));
      };

      img.src = e.target.result;
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Convert multiple image files to WebP format
 * @param {File[]} files - Array of image files to convert
 * @param {number} quality - Quality of the WebP images (0-1), default 0.9
 * @returns {Promise<File[]>} - Array of converted WebP files
 */
export const convertMultipleToWebP = async (files, quality = 0.9) => {
  const conversions = files.map((file) => convertToWebP(file, quality));
  return Promise.all(conversions);
};
